// Canonical source for all three worker apps. scripts/sync-house-ads.mjs
// generates the tools/blog copies; edit this root file only.

/**
 * GlitchReplay client setup shared by the main app, apps/tools and apps/blog:
 * the Sentry SDK init (errors + session replay), the noise filters tuned
 * against real GlitchReplay issues, and the dashboard sampling side-channel.
 * Each app's instrumentation-client.ts calls initGlitchReplay() and adds its
 * own extra integrations.
 */
import * as Sentry from "@sentry/react";
import {
  GLITCHREPLAY_HOST,
  GLITCHREPLAY_PROJECT_ID,
  GLITCHREPLAY_PUBLIC_KEY,
} from "./glitchreplay-dsn";

const PROJECT_ID = GLITCHREPLAY_PROJECT_ID;
const PUBLIC_KEY = GLITCHREPLAY_PUBLIC_KEY;
// The Sentry SDK DSN carries project id `0` — GlitchReplay maps the public
// key to the real project server-side; the REST calls below use PROJECT_ID.
const DSN = `https://${PUBLIC_KEY}@${GLITCHREPLAY_HOST}/0`;

// Replay privacy/redaction flags. Static defaults match the dashboard's
// "balanced" level (Settings → Replays → Privacy level on glitchreplay.com).
// Subsequent page loads use whatever the dashboard slider was last set to
// — cached from the previous /config fetch below. rrweb masks at snapshot
// time, so a change in the dashboard takes effect at the *next* page load,
// not mid-session.
const PRIVACY_CACHE_KEY = `gr:privacy:${PROJECT_ID}`;
export type PrivacyFlags = {
  maskAllText: boolean;
  maskAllInputs: boolean;
  blockAllMedia: boolean;
};
const DEFAULT_PRIVACY: PrivacyFlags = {
  maskAllText: false,
  maskAllInputs: true,
  blockAllMedia: true,
};
function readCachedPrivacy(): PrivacyFlags {
  if (typeof localStorage === "undefined") return DEFAULT_PRIVACY;
  try {
    const raw = localStorage.getItem(PRIVACY_CACHE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<PrivacyFlags>;
      return {
        maskAllText:
          typeof p.maskAllText === "boolean"
            ? p.maskAllText
            : DEFAULT_PRIVACY.maskAllText,
        maskAllInputs:
          typeof p.maskAllInputs === "boolean"
            ? p.maskAllInputs
            : DEFAULT_PRIVACY.maskAllInputs,
        blockAllMedia:
          typeof p.blockAllMedia === "boolean"
            ? p.blockAllMedia
            : DEFAULT_PRIVACY.blockAllMedia,
      };
    }
  } catch {}
  return DEFAULT_PRIVACY;
}

const NOISE_MESSAGES = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
  // Browser-extension content scripts that double-inject on SPA route changes.
  // We do not declare ScreenshotConfig anywhere in our bundle.
  "Identifier 'ScreenshotConfig' has already been declared",
  "Identifier 'originalQuery' has already been declared",
  // Chromium-only extension code injected into WebKit; our bundle never uses chrome.
  "Can't find variable: chrome",
  // Wallet-extension provider rejections — never reachable from our app.
  "Failed to connect to MetaMask",
  // History API protected by an extension wrapper; we never reassign pushState.
  "Cannot assign to read only property 'pushState' of object",
  // Minified React #310 ("Rendered more hooks than during the previous
  // render") with no in-app stack frames — extension-injected DOM mutating
  // between renders. Filtered when stack-less; real cases will retain a
  // stack and be kept by the existing beforeSend logic.
  "Minified React error #310",
  // Cloudflare Zaraz worker-port teardown: when the Zaraz worker's
  // session-keyed port ("content {uuid}") is closed mid-flight (navigation
  // or worker restart), Zaraz throws this from the main thread. The stack
  // frame URL is the page itself, not a zaraz script, so denyUrls misses it.
  "Port 'content ",
  // Chrome extension runtime messaging error: an extension's onMessage listener
  // returned true (async intent) but the message port closed before sendResponse
  // fired (tab navigation, extension update, worker teardown). Chrome leaks this
  // as an Error into the page context even though it originates in the extension
  // runtime — our code never uses chrome.runtime.onMessage.
  "the message channel closed before a response was received",
  // iOS WebView/native shell bridge noise. We never define or call this
  // handler; it is injected by the host browser/app and can be absent.
  "top.webkit.messageHandlers.foregroundToBackground.postMessage",
  // Google AdSense / GTM scripts try to postMessage to an iframe element
  // that was blocked by CSP or garbage-collected before the callback fires.
  // None of our code calls contentWindow.postMessage — always third-party
  // minified code (variable `e` = the iframe ref). denyUrls misses it when
  // the script is inlined or the frame URL differs from the script URL.
  "null is not an object (evaluating 'e.contentWindow.postMessage')",
  // rrweb (bundled inside Sentry Replay) accesses the cross-origin iframe's
  // Window.Element constructor to type-check DOM nodes during snapshotting.
  // The browser blocks this with a SecurityError whenever the page contains a
  // cross-origin iframe (AdSense, HubSpot, Google Maps). None of our code
  // reads contentWindow properties; denyUrls misses it because rrweb is
  // bundled in our own _next/static/chunks/ URL space.
  "Blocked a frame with origin",
  // rrweb also walks live iframes during snapshotting and accesses
  // contentWindow.document. When an AdSense/HubSpot iframe is removed from
  // the DOM between the rrweb walk and the property access, contentWindow is
  // null and throws this TypeError. Same root cause as "Blocked a frame with
  // origin" above; denyUrls misses it for the same reason.
  // Chrome format:
  "Cannot read properties of null (reading 'document')",
  // Firefox format (same root cause):
  'can\'t access property "document", a.contentWindow is null',
  // Safari format (same root cause):
  "null is not an object (evaluating 'a.contentWindow.document')",
];

// Stack-less or third-party-stack rejection messages thrown from embedded
// widgets / browser quirks rather than our code. Drop when there's no
// usable stack — same logic as the existing "Failed to fetch" filter.
const NOISE_REJECTION_MESSAGES_NO_STACK = [
  "Failed to fetch",
  // Firefox transient network error during cancelled fetch / offline.
  "NetworkError when attempting to fetch resource.",
  // DOMException { name: "NetworkError", message: "A network error occurred." } —
  // emitted by some browsers (older Safari, WebKit variants) when fetch() fails
  // at the network layer. Same class as "Failed to fetch" / "Load failed".
  "A network error occurred.",
  // Safari transient network error.
  "Load failed",
  // Apollo / HubSpot / Twilio Flex chat fetch wrappers throw these on
  // upstream blips; without a stack pointing into our code there's
  // nothing to do on our side.
  /^Response not (ok|successful)/i,
  // PromiseRejectionEvent with no reason at all.
  "Unhandled promise rejection",
  // Sentry's synthetic message when a Promise rejects with a non-Error
  // value (plain object, primitive, DOM Event, etc). Without a stack there's
  // no way to attribute it to our code — typically extension content scripts
  // or 3rd-party widgets rejecting with raw error-shaped objects like
  // {code, message, status}. Counter-evidence: gr_5avcdmc (ascii-art-generator).
  // Two formats in the wild: older "Non-Error promise rejection captured with
  // value: …" and newer "Object captured as promise rejection with keys: …"
  // (Sentry SDK v7+ uses the latter when the rejected value is an object with
  // enumerable keys). Both are stack-less noise from third-party code.
  // Counter-evidence: gr_syaxfpj (2 occurrences, 2026-06-16).
  /^Non-Error promise rejection captured/i,
  /^Object captured as promise rejection/i,
  // Sentry SDK v7+ format when the rejected value is a DOM Event instance
  // (e.g. a plain Event with type="error" from <img>/<script>/<link> onerror
  // firing inside a third-party promise). Our own onerror handlers always
  // wrap in new Error(); this pattern is exclusively extension/widget noise.
  /^Event `[^`]+` \(type=[^)]+\) captured as promise rejection$/,
  // Safari's SyntaxError text for invalid URL/regex parse — thrown by
  // `new URL("...")`, `decodeURIComponent("%")`, PaymentRequest, WebAuthn
  // and a handful of Safari-internal APIs. With no stack it's invariably
  // an extension or Safari API call we don't own. Chrome's equivalent
  // ("Invalid URL") never makes it through stack-less and the same code
  // path fingerprints differently there.
  // Counter-evidence: gr_7b9x43k (nist-800-88, Safari 26.5).
  "The string did not match the expected pattern.",
  // Chrome's window.onerror message for `throw ""` (throwing an empty string).
  // Chrome formats the onerror msg as "Uncaught " + String(thrownValue), so
  // throwing "" produces exactly this string (note the trailing space).
  // With no usable stack it is invariably a browser extension or third-party
  // widget — real application code always throws Error objects with messages.
  "Uncaught ",
];

// Third-party tracker / ad / analytics scripts. Errors thrown from frames
// inside these URLs are noise — filter them at the SDK boundary so they
// never hit the wire. Mirrors the legacy GlobalErrorHandler `noisePatterns`
// list, expanded with Zaraz / Cloudflare Insights observed in the wild.
export const NOISE_DENY_URLS = [
  /zaraz/i,
  /cloudflareinsights/i,
  /connect\.facebook\.net/i,
  /fbevents/i,
  /googletagmanager/i,
  /googlesyndication/i,
  // Google's ad-traffic-quality beacon, loaded by AdSense. The path is a
  // random per-impression string (ep2.adtrafficquality.google/ohxwkmpnad?...),
  // so every ad-blocked load fingerprints as a BRAND NEW issue — 25 of the 50
  // issues in one GlitchReplay digest were this single beacon. Nothing we ship
  // and nothing we can fix: ~28% of visitors block it (see the ad-block
  // measurement note). Filter by host so the fingerprint explosion stops.
  /adtrafficquality\.google/i,
  /fundingchoicesmessages/i,
  /google-analytics/i,
  /clarity\.ms/i,
  /js\.hs-(scripts|banner|analytics|adspixel)/i,
  /js\.hscollectedforms/i,
  /js\.hubspot/i,
  /js\.usemessages/i,
  /analytics\.ahrefs/i,
  /fonts\.googleapis\.com\/css\?family=Roboto(?:%3A|:)500/i,
  /fonts\.googleapis\.com\/css\?family=Google(?:%20|\+)Sans/i,
  // Embedded Google APIs (gapi loader, Translate widget, gstatic asset host).
  // These don't originate from our pages — usually injected by Translate
  // browser extensions or scraper previews.
  /apis\.google\.com/i,
  /translate\.googleapis\.com/i,
  /gstatic\.com\/.*\/translate/i,
  // Public CDN runtimes pulled in by user-injected snippets / sandboxes
  // (jsdelivr Recharts/jsPDF/prop-types failures appear when an embedder
  // rate-limits or strips the script).
  /cdn\.jsdelivr\.net/i,
  /cdnjs\.cloudflare\.com\/ajax\/libs\/mathjax\//i,
  // Browser-extension trackers that piggyback on pages.
  /kp\.apiget\.ru/i,
  /gtmpx\.com/i,
  /scriptcdn\.net/i,
  /amazingrefresh\.com/i,
  /zmstat\.com/i,
  // Third-party icon CDN — replaced with inline SVG / local assets where
  // we used it; remaining refs come from cached pages or scraped previews.
  /img\.icons8\.com/i,
  // OpenStreetMap tile CDN used by Leaflet maps — individual tile requests
  // fail transiently (CDN hiccups). The browser fires an img error event
  // which GlitchReplay captures; there is no actionable fix on our side.
  /tile\.openstreetmap\.org/i,
  // Grow.me (Mediavine publisher platform) scripts dynamically injected by
  // AdSense / GTM ad tags. faves.grow.me/main.js fails when the CDN is
  // unreachable or the visitor's network blocks it — not code we ship.
  /grow\.me/i,
  // Cloudflare Zaraz proxies GA through the site's own domain at /ga/<token>.
  // Network-layer ad-blockers / DNS filters catch even same-domain proxies,
  // producing resource_error noise identical to a direct third-party block.
  /inventivehq\.com\/ga\//i,
  // SSL-checker tool proxies to an external Azure Function; its own 5xx is an
  // upstream failure we don't own (the tool surfaces a clean error to the user).
  /ssl-checker-func\.azurewebsites\.net/i,
];

// Status-aware verification for first-party Cloudflare Images
// (imagedelivery.net) resource_error reports. networkProbeIntegration's
// per-URL probe uses mode:'no-cors', which resolves on ANY response —
// including a 404 from a deleted/renamed image id — so its
// `gr.probe=reachable` survivors still include real breakage AND pure
// client-side noise (a non-rendering JS agent or transient blip whose
// <img>/<link> fired `error` while the asset is actually fine; e.g. the
// clean-UA Firefox/Linux client in gr_vsm79dn/gr_wvbvag3/gr_pywqzvz).
// imagedelivery.net sends `access-control-allow-origin: *`, so a cors
// fetch CAN read the real status: returns true only on a genuine HTTP 200,
// letting beforeSend drop the noise while a 404 (or an unverifiable fetch
// failure) is kept so real broken images still surface. Memoized per URL.
const imageOkMemo = new Map<string, Promise<boolean>>();
function verifyImageOk(url: string): Promise<boolean> {
  const cached = imageOkMemo.get(url);
  if (cached) return cached;
  if (typeof fetch !== "function") {
    const p = Promise.resolve(false);
    imageOkMemo.set(url, p);
    return p;
  }
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), 4_000) : null;
  const p = fetch(url, {
    mode: "cors",
    cache: "no-store",
    credentials: "omit",
    signal: controller?.signal,
  })
    .then((res) => res.ok)
    .catch(() => false)
    .finally(() => {
      if (timer) clearTimeout(timer);
    });
  imageOkMemo.set(url, p);
  return p;
}

type ExtraIntegrations = NonNullable<
  Exclude<Parameters<typeof Sentry.init>[0], undefined>["integrations"]
>;

/**
 * Start error tracking and session replay.
 *
 * `privacy: "dashboard"` (default) uses the flags last cached from the
 * GlitchReplay dashboard, falling back to "balanced". Pass explicit flags to
 * pin them instead. The workers share the inventivehq.com origin, so the
 * dashboard cache in localStorage is shared too: a surface that must stay
 * strict (the tools, where visitors paste hashes and ciphertext) pins its
 * flags so a cached "balanced" setting can never loosen them.
 */
export function initGlitchReplay({
  privacy = "dashboard",
  integrations = [],
}: {
  privacy?: "dashboard" | PrivacyFlags;
  integrations?: Extract<ExtraIntegrations, unknown[]>;
} = {}): void {
  const privacyFlags = privacy === "dashboard" ? readCachedPrivacy() : privacy;

  Sentry.init({
    dsn: DSN,
    release:
      process.env.NEXT_PUBLIC_RELEASE ||
      process.env.NEXT_PUBLIC_COMMIT_SHA ||
      "dev",
    // Always-on session replay. Buffer mode (`replaysSessionSampleRate: 0`
    // + `replaysOnErrorSampleRate: 1.0`) silently drops first-frame errors:
    // the rrweb full snapshot is queued asynchronously, so when a React
    // hydration error (e.g. #418) fires during the first commit the buffer
    // is empty and the on-error flush sends nothing. Session mode starts
    // the recorder synchronously at init and uploads segments continuously,
    // so even errors thrown before the first snapshot land still produce a
    // viewable replay (post-error frames at minimum). Counter-evidence:
    // gr_xkggqbf, financialtoolset.com, 2026-04-27.
    //
    // Record every session. GlitchReplay's priority sweep decides what to
    // keep once a session goes quiet: bots and sessions with no interaction
    // are dropped, errored ones kept, the rest sampled. A client-side 0.05
    // threw 95% away at random before the server could judge any of it.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    denyUrls: NOISE_DENY_URLS,
    integrations: [
      Sentry.replayIntegration({
        ...privacyFlags,
        // rrweb's dead/slow-click heuristic flags a click as "dead" when no
        // DOM mutation follows within slowClickTimeout. On article prose,
        // tables, code blocks and headings a click is text selection, never an
        // action — pure false positives that have made up ~half of past
        // GlitchReplay batches. Ignore those containers; real interactive
        // elements still get dead-click detection (rage-click is unaffected).
        //
        // Form fields (input/select/textarea) self-focus on click without
        // mutating the DOM, so rrweb marks the click "dead" even though it
        // succeeded. Same for the site logo (an <img> nested in <Link>) —
        // navigation happens after replay capture, looking like a dead click.
        // h1/h2 headings are user-selected for copy/share; never an action.
        // Disabled buttons (`[disabled]`) are intentionally inert. The hero
        // wrapper `.hero-entrance` is a Framer-Motion animation container
        // where clicks land between the heading and the CTA.
        slowClickIgnoreSelectors: [
          ".prose",
          ".prose *",
          '[aria-label="Table"]',
          '[aria-label="Table"] *',
          ".heading-with-anchor",
          "pre",
          "pre *",
          "table",
          "table *",
          "input",
          "select",
          "textarea",
          'img[alt="Inventive HQ"]',
          // Same reason as the logo above, generalised: a click on any real
          // link navigates, and the navigation happens after replay capture
          // stops, so rrweb sees no mutation and calls it dead. Every dead-click
          // issue we have raised against an <a> has been this false positive
          // (bce893eb6f22992f, 8b19df12d6f815df).
          "a[href]",
          "a[href] *",
          "h1",
          "h2",
          "h3",
          "[disabled]",
          ".hero-entrance",
          ".hero-entrance *",
        ],
      }),
      // Captures fetch/XHR 5xx responses. The browser auto-logs failed
      // requests to the console, but `fetch()` doesn't reject on HTTP error
      // status, so no exception or unhandled rejection fires — default
      // integrations miss them. Defaults: status [[500, 599]], targets /.*/.
      Sentry.httpClientIntegration(),
      ...integrations,
    ],
    beforeSend(event, hint) {
      const message =
        event.message ||
        (hint?.originalException as Error | undefined)?.message ||
        "";
      if (NOISE_MESSAGES.some((n) => message.includes(n))) return null;
      // google-anno / data-google-vignette in the message means the violation
      // target is a Google-injected annotation/vignette element — not our DOM.
      // Belt-and-suspenders alongside the a11yTarget check below, for events
      // where event.contexts?.a11y is absent. data-google-vignette covers the
      // <a class="google-anno"> variant whose message omits the class name.
      if (/google-anno|data-google-vignette/i.test(message)) return null;
      // Social-share / contact browser extensions inject category containers with
      // inline letter/word-spacing (aria-label="Email & Messaging", "Social Networks",
      // etc.). Belt-and-suspenders: the a11yTarget check below catches these when
      // event.contexts?.a11y is present; this message-level guard catches the same
      // events when that context is absent.
      if (
        /aria-label="(?:Email &amp; Messaging|Email & Messaging|Social Networks|Computer Security)"/i.test(
          message,
        )
      )
        return null;
      // Browser extensions (hardware-info, entity-recognition) wrap tech terms
      // like "Processor" in <span title="..."> with inline letter/word-spacing.
      // That span is not in our authored DOM. Belt-and-suspenders alongside the
      // a11yTarget check below.
      if (/span\[title="Processor"\]/i.test(message)) return null;
      // Browser-extension DOM injections (ChatGPT sidebar, Acrobat "Get summaries"
      // button, Grammarly, LastPass, Honey, floating avatar buttons, RE:'s
      // quick-control-panel) surface as a11y violations whose target lives outside
      // our authored DOM. Belt-and-suspenders alongside the a11yTarget check below,
      // for events where event.contexts?.a11y is absent.
      if (
        /chatgpt-sidebar|aiFabShadowRoot|acrobat-button|grammarly-|lastpass-|honey-extension|floatBtnAvatar|re-quick-control-panel/i.test(
          message,
        )
      ) {
        return null;
      }
      // Page proxies that rewrite the DOM (Google Translate's *.translate.goog,
      // Yandex Translate, generic *.translate.* mirrors) trigger recursion in
      // rrweb / IntersectionObserver chains we don't control. The frame URLs
      // point at the proxy host, so denyUrls (which matches script source) misses
      // them — filter on the event request URL here instead.
      const requestUrl = event.request?.url || "";
      if (/\.translate\.goog/i.test(requestUrl)) return null;
      // Reading/anonymizing proxies (Startpage "Anonymous View" at
      // av.startpage.com/__av/<base64-url>, and translate mirrors) re-serve our
      // page from their own origin, rewriting every asset URL into the proxy host.
      // That breaks image loads, fetches (502/503) and React hydration (#418) in
      // ways we can't act on — the request URL AND the stack frame filenames point
      // at the proxy, not our code. Match across both. Counter-evidence:
      // gr_3dx3pz3/gr_mwk3xpj/gr_rg5kx8g/gr_6w8txgm (one Startpage session).
      const proxyHaystack = [
        requestUrl,
        event.exception?.values?.[0]?.value || "",
        ...(event.exception?.values ?? [])
          .flatMap((v) => v.stacktrace?.frames ?? [])
          .map((f) => f.filename || ""),
      ].join(" ");
      if (/av\.startpage\.com\/__av\/|\.translate\.goog/i.test(proxyHaystack)) {
        return null;
      }
      // axe a11y events whose target / html lives inside a browser extension's
      // injected shadow root or overlay: Adobe Acrobat reader extension's
      // "Get summaries..." floating button (`#aiFabShadowRoot,.acrobat-button`),
      // OpenAI ChatGPT-sidebar extension's draggable handle (`chatgpt-sidebar`).
      // Neither lives in our DOM tree, but axe-core walks document.* including
      // injected nodes, so they surface as link-name / button-name / nested-
      // interactive failures. Counter-evidence: gr_zd5jcrk (Acrobat, blog),
      // gr_2gjne3p (ChatGPT sidebar, firewall-rule-simulator).
      const a11yEvent = event.contexts?.a11y as
        { target?: string[]; html?: string } | undefined;
      if (a11yEvent) {
        // color-contrast is the dominant axe false-positive in production RUM:
        // it fires on clean sessions where the combo passes WCAG AA on a solid
        // background, but axe can't resolve composited/gradient/overlay
        // backgrounds — and every flag drives non-actionable auto-fix churn. Our
        // Tailwind palette is verified AA (gray-500/white 4.83:1, gray-400/
        // gray-800 5.78:1, red-800/red-50 7.6:1, red-600/white 4.83:1, etc.), so
        // drop color-contrast in PRODUCTION only. The axe scanner still reports
        // it on development/staging where a dev can inspect with full context.
        const isProduction =
          typeof window !== "undefined" &&
          window.location.hostname === "inventivehq.com";
        if (isProduction && /color-contrast/i.test(event.message || "")) {
          return null;
        }
        const a11yTarget = Array.isArray(a11yEvent.target)
          ? a11yEvent.target.join(" ")
          : "";
        const a11yHtml = a11yEvent.html || "";
        if (
          /chatgpt-sidebar|aiFabShadowRoot|acrobat-button|grammarly-|lastpass-|honey-extension|floatBtnAvatar|re-quick-control-panel/i.test(
            a11yTarget,
          )
        ) {
          return null;
        }
        // Google AdSense Auto Ads injects a bottom anchor ad unit as #bottomBar
        // (and its child images / tracking pixels). These are AdSense-owned nodes
        // that we cannot annotate with alt text. Same class as the browser-
        // extension injection filters above.
        // AdSense also creates numbered iframe host elements (#aswift_N_host) with
        // internal classes (.google-aiuf, .goog-rentries) that carry inline spacing
        // styles — avoid-inline-spacing fires on those injected nodes, not our DOM.
        if (
          /^#bottomBar\b/i.test(a11yTarget) ||
          /aswift_\d+_host/i.test(a11yTarget)
        ) {
          return null;
        }
        // Password managers / browser autofill (LastPass, Dashlane, 1Password,
        // Bitwarden, Chrome/Edge autofill) tag form controls with marker
        // attributes — most visibly `fdprocessedid` — and reflow them, so axe
        // recomputes contrast/target-size against the mutated node and reports
        // false positives. The marker is in the injected HTML, never in our
        // authored markup. Counter-evidence: the gr_*/fdprocessedid color-contrast
        // cluster on /blog/...vlsm... (buttons that measure >=4.5:1 unmutated).
        if (
          /fdprocessedid|data-(lastpass|dashlane|1p|1password|bitwarden|bw)\b/i.test(
            a11yHtml + " " + a11yTarget,
          )
        ) {
          return null;
        }
        // In-page Google Translate wraps every text node in
        // <font dir="auto" style="vertical-align: inherit;"> and sometimes
        // nests those wrappers. Axe then reports violations against the
        // injected <font> nodes instead of our authored DOM. The HTML / target
        // fields carry that fingerprint. Same root cause as the #418 hydration
        // filter above. Counter-evidence: gr_p4mvf8z (url-expander, blog
        // comments), gr_p7zr6n1 (color-contrast, nested Translate font).
        if (
          /<font[^>]+dir="auto"[^>]*>\s*<\/font>/i.test(a11yHtml) ||
          (/> font(?::[^\s>]+)? > font/i.test(a11yTarget) &&
            /<font\b/i.test(a11yHtml))
        ) {
          return null;
        }
        // Google's iOS Search App (GSA in-app browser) and Chrome's
        // "annotations" feature wrap recognized entities in
        // <span class="google-anno-sc google-anno-skip" aria-label="...">
        // and apply inline letter/word-spacing. Axe flags that injected
        // markup as avoid-inline-spacing (and the synthetic aria-labels trip
        // related rules), but the nodes are added by Google's app after
        // paint — they are not in our authored DOM. Same class as the Google
        // Translate <font> filter above. Counter-evidence: gr_j8fes43,
        // gr_uqh3kv7 et al. (caesar-cipher, GSA iOS UA — one cluster of ~19).
        if (
          /google-anno/i.test(a11yTarget) ||
          /google-anno/i.test(a11yHtml) ||
          /data-google-vignette/i.test(a11yTarget) ||
          /data-google-vignette/i.test(a11yHtml)
        ) {
          return null;
        }
        // Social-share / contact browser extensions inject containers like
        // <div aria-label="Social Networks"> or <div aria-label="Email & Messaging">
        // with <span> children that carry inline letter/word-spacing,
        // triggering avoid-inline-spacing. The nodes are not in our authored DOM.
        if (
          /aria-label="Social Networks"/i.test(a11yTarget) ||
          /aria-label="Email & Messaging"/i.test(a11yTarget) ||
          /aria-label="Computer Security"/i.test(a11yTarget)
        ) {
          return null;
        }
        // Browser extensions (hardware-info annotators, entity-recognition) wrap
        // recognised tech terms in <span title="Processor"> (and similar) with
        // inline letter/word-spacing — not our authored DOM.
        if (/span\[title="Processor"\]/i.test(a11yTarget)) {
          return null;
        }
      }
      // Cloudflare Rocket Loader (cdn-cgi/scripts/.../rocket-loader.min.js)
      // defers and re-injects every <script> on the page; when it interacts
      // with React it crashes mid-commit with "Cannot read properties of null
      // (reading 'offsetHeight' / 'tagName')" or surfaces AbortError from its
      // own fetch wrappers. The first frame is always inside the rocket-loader
      // chunk — that's not code we ship. The proper fix is to disable Rocket
      // Loader in the Cloudflare dashboard; the filter keeps the report queue
      // clean until that lands. Counter-evidence: gr_8tdaer4 (hash-lookup,
      // offsetHeight), gr_4qpqkmy (ascii-art-generator, AbortError).
      const firstFrame =
        event.exception?.values?.[0]?.stacktrace?.frames?.slice(-1)[0];
      if (
        firstFrame?.filename &&
        /\/cdn-cgi\/scripts\/[^/]+\/cloudflare-static\/rocket-loader/i.test(
          firstFrame.filename,
        )
      ) {
        return null;
      }
      // Web Worker importScripts NetworkError from a blob: source — browser
      // extensions (ad blockers, accessibility helpers) sometimes spawn a
      // Worker from `URL.createObjectURL(new Blob(...))` and then race the
      // page lifecycle: navigation revokes the parent blob before the worker
      // imports its sibling chunk. The error's only frame is a blob: URL we
      // don't own. Counter-evidence: gr_7nc2bxq.
      if (
        firstFrame?.filename?.startsWith("blob:") &&
        /importScripts.*WorkerGlobalScope/i.test(
          event.exception?.values?.[0]?.value || "",
        )
      ) {
        return null;
      }
      // Browser-extension content scripts wrap window.addEventListener to
      // observe events (password managers, ad-blockers, accessibility tools).
      // Their hook function `addEL_hook` shows up as an anonymous frame with
      // no filename when the wrapped React handler later throws. We can't fix
      // a third-party hook from our code; same class as the rocket-loader
      // filter. Counter-evidence: gr_jq3xcmw (spf-10-dns-lookup-limit blog,
      // tagName null with addEL_hook in the stack).
      const stackFrames =
        event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      if (stackFrames.some((f) => /addEL_hook/i.test(f.function || ""))) {
        return null;
      }
      // Resource error for a <script>/<link>/<img> URL that lives in the
      // NOISE_DENY_URLS list. denyUrls already drops errors WHOSE STACK matches
      // those scripts, but a resource_error event has no stack — the URL is in
      // exception.values[0].value as "Failed to load <script>: <URL>". Match
      // on that string so resource-load failures from the same allow-list are
      // filtered consistently. Counter-evidence: gr_x8q2hru
      // (domain-spoofing-detector, jsdelivr jspdf load fail injected by an
      // extension).
      // NOTE: the type===resource_error guard was removed because Sentry SDK
      // sets type="Error" for <img> load failures (e.g. OSM tile CDN hiccups)
      // while only using "resource_error" for <script>/<link> failures. The
      // two remaining conditions are specific enough to avoid false positives.
      const resourceErrorValue =
        event.exception?.values?.[0]?.value || message || "";
      // An empty injected <audio src=""> or <img src=""> resolves to the document URL.
      if (
        resourceErrorValue === `Failed to load <audio>: ${requestUrl}` ||
        resourceErrorValue === `Failed to load <img>: ${requestUrl}`
      ) {
        return null;
      }
      if (
        /Failed to load <(script|link|img)>:/i.test(resourceErrorValue) &&
        NOISE_DENY_URLS.some((re) => re.test(resourceErrorValue))
      ) {
        return null;
      }
      // HTTP Client errors (fetch/XHR 5xx) from noise-listed URLs.
      // denyUrls only matches stack frames; httpClientIntegration sets
      // event.request.url to the failing URL, which denyUrls doesn't check.
      const httpClientErrorValue =
        message || event.exception?.values?.[0]?.value || "";
      if (
        /^HTTP Client Error with status code:/i.test(httpClientErrorValue) &&
        (NOISE_DENY_URLS.some((re) => re.test(event.request?.url || "")) ||
          new RegExp(
            `^https://${GLITCHREPLAY_HOST.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}/`,
          ).test(event.request?.url || ""))
      ) {
        return null;
      }
      // 500/502/503 from our own tool-proxy endpoints that aggregate flaky third-party
      // data providers (breach lookups, IP geolocation, Twilio chat, CORS analyzer,
      // TLS certificate fetchers, DNS/MX lookups, HuggingFace models, robots.txt
      // fetcher, IP risk checker). Also covers form-submission proxies (contact,
      // software-quote, network-opportunity) whose upstream is PushMail, and D1-backed routes
      // (gpu-benchmarks, tools/cve/*) where query failures surface as 500 — same
      // pattern: every route catches the exception and returns a clean error payload,
      // so the 5xx reflects a degraded upstream or backing store, not an app fault.
      // Counter-evidence: gr_fegf74m/gr_5a37ejp (breach-check 502),
      // gr_6t725mv (ip-geolocation 503), chat 502 (Twilio Assistant downtime).
      if (
        /^HTTP Client Error with status code: 50[023]\b/i.test(
          httpClientErrorValue,
        ) &&
        /\/api\/(breach-check|ip-geolocation|chat|cors-analyzer|fetch-certificate|fetch-issuer-cert|dns\/mx|hf-models|fetch-robots-txt|ip-risk|password-breach-check|contact|software-quote|network-opportunity|network-availability|email|security-headers|whois|geocode|gpu-benchmarks|wordlist|tools\/cve\/kev|tools\/cve\/severity|tools\/cve\/year)\b/i.test(
          event.request?.url || "",
        )
      ) {
        return null;
      }
      // React reconciler crash from external DOM mutation: in-page browser
      // translation (Chrome iOS / Safari Translate) or an extension relocates
      // a node, then commit-phase insertBefore/removeChild/appendChild throws
      // NotFoundError. Not our bug — and the page keeps its real URL, so the
      // *.translate.goog check above can't catch it. In production the React
      // commit functions are minified (uf/uh/uy…), so the original `f.function`
      // regex never matched a real build — key off the error message, which the
      // DOM API always spells out in full ("Failed to execute 'removeChild' on
      // 'Node': The node to be removed is not a child of this node.").
      // Counter-evidence: gr_cyxmqrr (CT-lookup, CriOS), gr_yks354y (xor-cipher).
      const domMutationValue =
        event.exception?.values?.[0]?.value || message || "";
      if (
        (event.exception?.values?.[0]?.type === "NotFoundError" ||
          /Failed to execute '(insertBefore|removeChild|appendChild|replaceChild)'/i.test(
            domMutationValue,
          ) ||
          // Firefox encodes the error name into the message with type="Error":
          // "NotFoundError: Node.removeChild: The node to be removed is not a child of this node"
          /not a child of this node/i.test(domMutationValue) ||
          /The object can ?not be found here/i.test(domMutationValue)) &&
        (/Failed to execute '(insertBefore|removeChild|appendChild|replaceChild)'/i.test(
          domMutationValue,
        ) ||
          /not a child of this node/i.test(domMutationValue) ||
          /The object can ?not be found here/i.test(domMutationValue) ||
          (event.exception?.values?.[0]?.stacktrace?.frames ?? []).some((f) =>
            /insertBefore|removeChild|appendChild/i.test(f.function || ""),
          ))
      ) {
        return null;
      }
      // React hydration mismatches (#418 HTML/text, #423/#425 server text)
      // with a stack that lives entirely inside React's compiled chunk —
      // no app frames at all. Our pages are static / RSC-rendered with no
      // dynamic client-side text that could legitimately diverge from the
      // server output, so when we see one of these it's almost always
      // in-page browser translation (Chrome iOS / Safari Translate /
      // Android Chrome's auto-translate) rewriting text nodes between SSR
      // and the first commit. Same class as the insertBefore/removeChild
      // filter above; #418 just lands earlier in the commit phase before
      // any DOM-API call throws. Counter-evidence: gr_yu5snvm
      // (ascii-art-generator, Android Chrome auto-translate),
      // gr_g8nqkrj (hex-editor, iOS Safari).
      if (/Minified React error #(418|423|425)/i.test(domMutationValue)) {
        const stackFrames =
          event.exception?.values?.[0]?.stacktrace?.frames ?? [];
        const hasAppFrame = stackFrames.some(
          (f) =>
            !!f.filename &&
            !/_next\/static\/chunks\//.test(f.filename) &&
            !/\.translate\./.test(f.filename),
        );
        if (!hasAppFrame) return null;
      }
      // Webpack runtime "Cannot read properties of undefined (reading
      // 'call')" with the first frame inside webpack-*.js — this fires
      // when a stale tab navigates client-side and asks the runtime for a
      // module ID that doesn't exist in the freshly-loaded chunks. No app
      // code is at fault; the in-app frame is webpack's `__webpack_require__`
      // which we don't own. A hard reload would resolve it (handled in
      // app/error.tsx for caught cases), but most fire from background
      // navigation Promises and never reach the boundary. Counter-evidence:
      // gr_jws53hc (knowledge-base, 127 occurrences in 36 h — single
      // fingerprint, no app frames).
      if (
        event.exception?.values?.[0]?.type === "TypeError" &&
        /Cannot read properties of undefined \(reading 'call'\)|undefined is not an object \(evaluating 'e\[n\]\.call'\)|can't access property "call", e\[n\] is undefined/i.test(
          domMutationValue,
        )
      ) {
        const firstFrame =
          event.exception?.values?.[0]?.stacktrace?.frames?.slice(-1)[0];
        if (
          firstFrame?.filename &&
          /\/_next\/static\/chunks\/webpack-/.test(firstFrame.filename)
        ) {
          return null;
        }
      }
      // Loading-chunk failures during client-side navigation: same stale-
      // tab class, surfaces as "Loading chunk N failed" with the chunk
      // URL in the message. app/error.tsx detects this and shows a
      // recovery UI, but Promise-internal failures never reach the
      // boundary. Counter-evidence: gr_8r2zqx5.
      if (/^Loading chunk \d+ failed\./i.test(domMutationValue)) {
        return null;
      }
      // SEO / archive crawlers that execute JS (SEBot, AhrefsBot, SemrushBot,
      // etc.) sometimes mutate the DOM out from under React's commit phase,
      // producing reconciler errors like "Cannot read properties of null
      // (reading 'firstChild')" from inside the React chunk with no app frames.
      // These aren't real user-facing bugs — same class as the translate.goog
      // filter above. Counter-evidence: gr_m4djqk4 (SEBot-WA, world-clock).
      // `notebooklm` covers Google-NotebookLM, an AI page-fetcher whose UA
      // carries no "bot"/"crawler" token. It doesn't load images, so it emits
      // bogus imagedelivery.net resource_error reports (gr_3w9kx94 et al.) —
      // verified the images return HTTP 200 in a real browser. `adsense` covers
      // GoogleAdSenseInfeed, Google's AdSense in-feed page renderer (UA ends
      // "…Galaxy Nexus…GoogleAdSenseInfeed"), which emits transient
      // "Connection closed" errors (gr_3y8tv2y).
      const ua =
        (event.request?.headers as Record<string, string> | undefined)?.[
          "User-Agent"
        ] || "";
      // `mediapartners` = Mediapartners-Google, the AdSense crawler. Its UA
      // carries no "bot"/"crawler"/"adsense" token, so it slipped through and
      // generated entire a11y clusters (e.g. the 17 world-clock target-size
      // reports, gr_23mgcj3 et al.) plus transient "Connection closed" errors.
      if (
        /bot|crawler|spider|scraper|headlesschrome|notebooklm|adsense|mediapartners/i.test(
          ua,
        )
      )
        return null;
      // Bare "Connection closed." with no other context is the same
      // GoogleAdSenseInfeed/Mediapartners-Google noise documented above, just
      // missing a matching UA token this time. Message-only fallback so it's
      // still caught. Counter-evidence: gr_3y8tv2y / gr_23mgcj3.
      if (/^Connection closed\.?$/i.test(domMutationValue)) return null;
      // Next.js <Link> prefetches hashed chunk JS / CSS / fonts for routes
      // the user might navigate to. Those prefetches fail for reasons we
      // can't act on: a stale tab pointing at an asset hash that's been
      // replaced by a newer deploy (404), aggressive privacy extensions
      // blocking /_next/, or the browser aborting an in-flight prefetch
      // when the user navigates away. None block real navigation —
      // Next.js re-fetches on click. Real failures during actual
      // navigation surface as separate JS errors with stacks.
      // Counter-evidence: gr_rvr77q8/4q3dr46/8yz926e/xbnd2v6/wc9nmwf/j4rxd9g.
      const exValue = event.exception?.values?.[0]?.value || "";
      if (
        event.exception?.values?.[0]?.type === "resource_error" &&
        /_next\/static\/(chunks|css|media)\//.test(exValue)
      ) {
        return null;
      }
      // Google Translate widget assets failing to load — pure third-party
      // noise from in-page translation, never an actionable CDN failure.
      // Counter-evidence: gr_y7ptjs2 (gstatic translate_http CSS, CT-lookup).
      if (
        event.exception?.values?.[0]?.type === "resource_error" &&
        /translate_http|translate\.goog|\/_\/translate/i.test(
          exValue + " " + requestUrl,
        )
      ) {
        return null;
      }
      // Grow.me (Mediavine publisher platform) injected by AdSense/GTM.
      // The NOISE_DENY_URLS entry added in #104 only catches events where
      // value contains "Failed to load <script>:"; for resource_error type
      // events Sentry may store the URL in value without that prefix, or in
      // the event message/title/tags instead.
      const growMeResourceHaystack = [
        exValue,
        message,
        requestUrl,
        ...Object.values(event.tags ?? {}),
        JSON.stringify(event.extra ?? {}),
      ].join(" ");
      if (/grow\.me/i.test(growMeResourceHaystack)) {
        return null;
      }
      // AdblockBeacon's network bait script (static.doubleclick.net/instream/
      // ad_status.js — see components/AdblockBeacon.tsx) is DESIGNED to fail
      // when an ad/tracker blocker is present; that failure IS the detection
      // signal and is already handled by the component's own onerror handler.
      // WebKit sometimes surfaces the failed load as a bare "TypeError: Load
      // failed" (its generic network-error message) rather than the usual
      // "Failed to load <script>: <url>" resource_error shape, which slips
      // past both the stack-less NOISE_REJECTION_MESSAGES_NO_STACK match above
      // and the NOISE_DENY_URLS/denyUrls host filter (neither of which lists
      // static.doubleclick.net, since AdSense itself legitimately loads it).
      if (
        /^Load failed$/i.test((exValue || message || "").trim()) &&
        /static\.doubleclick\.net/i.test(
          [exValue, message, requestUrl].join(" "),
        )
      ) {
        return null;
      }
      // First-party Cloudflare Images resource_error that survived
      // networkProbeIntegration's no-cors probe (gr.probe=reachable). Re-verify
      // the real HTTP status via cors fetch: a true 200 means the element
      // failed to render but the asset is healthy (client-side noise) — drop
      // it; a 404/other status or an unverifiable fetch is kept so a genuinely
      // broken/renamed image still surfaces. See verifyImageOk above.
      if (
        event.exception?.values?.[0]?.type === "resource_error" &&
        /imagedelivery\.net/i.test(exValue)
      ) {
        const imgUrl = exValue.match(/https?:\/\/[^\s>]+/)?.[0];
        if (imgUrl) {
          return verifyImageOk(imgUrl).then((ok) => (ok ? null : event));
        }
      }
      // Stack-less rejections from embedded widgets / browser quirks: drop
      // the message when there's no usable stack so legitimate fetch
      // failures with a real call site still surface.
      const hasUsableStack = (event.exception?.values ?? []).some(
        (value) => (value.stacktrace?.frames?.length ?? 0) > 0,
      );
      if (!hasUsableStack) {
        // Sentry's synthetic "Non-Error promise rejection captured with
        // value: …" text lands in exception.values[].value, not event.message
        // (event.message is undefined for exception events) — fall back to it
        // so the patterns above can actually match. Without this, third-party
        // rejections like Outlook SafeLink's "Object Not Found Matching Id"
        // slip through. Counter-evidence: gr_8d3ypx8.
        const rejectionText =
          message || event.exception?.values?.[0]?.value || "";
        // Also check exception.values[0].value directly: when the rejected
        // object has its own .message property, `message` above captures it
        // and short-circuits the || so the Sentry synthetic description
        // ("Non-Error promise rejection captured with value: …") in
        // exception.values[0].value is never checked against the patterns.
        const exceptionValue = event.exception?.values?.[0]?.value || "";
        const matches = NOISE_REJECTION_MESSAGES_NO_STACK.some((p) =>
          typeof p === "string"
            ? rejectionText === p || exceptionValue === p
            : p.test(rejectionText) || p.test(exceptionValue),
        );
        if (matches) return null;
      }
      return event;
    },
  });

  // Automated browsers (Puppeteer, Playwright, Selenium) set
  // navigator.webdriver. The tag rides on the replay_event, and GlitchReplay's
  // priority sweep drops any session carrying it, whatever its user agent.
  if (navigator.webdriver) Sentry.setTag("automation", "1");

  // Dashboard-controlled session sampling. Static init above starts session
  // replay at 1.0 to guarantee first-frame error capture. This side-channel
  // lets the dashboard *down-sample* (or stop) the live recorder when a
  // project owner wants to dial back replay volume without redeploying — if
  // the dashboard returns `enabled: false` or a sub-1.0 sample rate that
  // loses the dice roll, we stop the in-progress recording.
  type ReplayCfg = {
    replay?: {
      enabled?: boolean;
      sessionSampleRate?: number;
      onErrorSampleRate?: number;
      maskAllText?: boolean;
      maskAllInputs?: boolean;
      blockAllMedia?: boolean;
    };
  };

  // Session-attempt beacon. Counts every SDK init for the dashboard's
  // errors/session denominator — the /config fetch below decides whether
  // to *persist* this recording, but every attempt should count. sendBeacon
  // survives pagehide so quick-bounce visitors are still tallied, and never
  // delays unload.
  try {
    navigator.sendBeacon(
      `https://${GLITCHREPLAY_HOST}/api/${PROJECT_ID}/session-attempt?sentry_key=${PUBLIC_KEY}`,
    );
  } catch {}

  fetch(
    `https://${GLITCHREPLAY_HOST}/api/${PROJECT_ID}/config?sentry_key=${PUBLIC_KEY}`,
    { cache: "no-store" },
  )
    .then(async (r): Promise<ReplayCfg | null> =>
      r.ok ? ((await r.json()) as ReplayCfg) : null,
    )
    .then((cfg) => {
      // Cache the dashboard's current privacy flags for next page load.
      if (
        cfg?.replay &&
        typeof cfg.replay.maskAllText === "boolean" &&
        typeof cfg.replay.maskAllInputs === "boolean" &&
        typeof cfg.replay.blockAllMedia === "boolean"
      ) {
        try {
          localStorage.setItem(
            PRIVACY_CACHE_KEY,
            JSON.stringify({
              maskAllText: cfg.replay.maskAllText,
              maskAllInputs: cfg.replay.maskAllInputs,
              blockAllMedia: cfg.replay.blockAllMedia,
            }),
          );
        } catch {}
      }

      const replay = Sentry.getReplay();
      if (!replay) return;
      if (!cfg?.replay?.enabled) {
        replay.stop();
        return;
      }
      const sessionRate = Number(cfg.replay.sessionSampleRate ?? 1);
      if (sessionRate < 1 && Math.random() >= sessionRate) {
        replay.stop();
      }
    })
    .catch(() => {
      // Network/CORS/whatever — leave the static 1.0 session recorder running.
    });
}
